import React, { useEffect } from 'react'
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form'

import './index.scss'
import clsx from 'clsx'
import { tableCellSanitizer } from '@App/helpers/table-cell-sanitizer'

interface CellInputProps {
    name: string
    defaultValue?: string | number | null
    readOnly?: boolean
    tooltipId?: string
    min?: number
    max?: number
    type: 'number' | 'text'
    onValueChanged?: (value: string | number) => void
    onClick?: () => void
}

const CellInput = ({
    name,
    defaultValue,
    readOnly,
    tooltipId,
    min,
    max,
    type,
    onValueChanged,
    onClick,
}: CellInputProps) => {
    const { control, setValue } = useFormContext()

    const typeIsANumber = type === 'number'

    const sanitizedValue = tableCellSanitizer(defaultValue, typeIsANumber ? '' : '--')

    const isNumber = (numberString: string) => !isNaN(parseFloat(numberString)) && !isNaN(parseInt(numberString, 10))

    const onChange = (field: ControllerRenderProps<FieldValues, string>, e: React.ChangeEvent<HTMLInputElement>) => {
        {
            const displayValue: string | number = e.target.value
            let value: string | number = displayValue

            if (isNumber(value as string)) {
                const parsedNumber = Number.parseFloat(e.target.value)
                if (min !== undefined && max && parsedNumber >= min && parsedNumber <= max) {
                    value = parsedNumber
                } else {
                    return
                }
            }

            field.onChange(displayValue)
            onValueChanged && onValueChanged(value)
        }
    }

    // setValue needs to be used to set the default value of the input field, using defaultValue on the Controller will not work as expected.
    // https://stackoverflow.com/questions/64306943/defaultvalues-of-react-hook-form-is-not-setting-the-values-to-the-input-fields-i
    useEffect(() => {
        setValue(name, sanitizedValue)
    }, [sanitizedValue, setValue])

    return (
        <>
            <Controller
                name={name}
                control={control}
                shouldUnregister
                defaultValue={sanitizedValue}
                render={({ field, fieldState }) => (
                    <div className="cell-input">
                        <div className="input-container" onClick={onClick}>
                            <input
                                className={clsx(fieldState.error && 'error', typeIsANumber && 'number-input')}
                                data-tooltip-id={fieldState.error && `${tooltipId}`}
                                readOnly={readOnly}
                                type="text"
                                min={min}
                                max={max}
                                {...field}
                                onChange={(e) => onChange(field, e)}
                            />
                            {!readOnly && <span className="percentage-sign">%</span>}
                        </div>

                        {fieldState.error && (
                            <span className="error-message">{fieldState.error.message as string}</span>
                        )}
                    </div>
                )}
            />
        </>
    )
}

export default CellInput
